import { Add, ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import CreateDripStepModal from "./CreateDripStepModal";
import DripStep from "./DripStep";
import {
  AUDIENCE_COUNT_ENDPOINT,
  AUDIENCE_SEGMENT_LIST_ENDPOINT,
  DRIP_CAMPAIGN_PUBLISH_ENDPOINT,
  DRIP_CAMPAIGN_SAVE_DRAFT_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import axios from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import BackButton from "../../common/back-button";

const TRIGGER_TYPE_OPTIONS = [
  { label: "Reply Based (Auto-progress if no reply)", value: "reply_based" },
];

const CreateDripMarketingCampaign = ({ onNavigate }) => {
  const [createDripStepModalVisible, setCreateDripStepModalVisible] =
    useState(false);

  const [campaignName, setCampaignName] = useState("");
  const [triggerType, setTriggerType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [description, setDescription] = useState("");

  const [dripSteps, setDripSteps] = useState([]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [segmentOptions, setSegmentOptions] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedSegmentAudienceCount, setSelectedSegmentAudienceCount] =
    useState(0);

  const { getValidToken } = useAuth();

  const addDripStep = (dripStepData) => {
    setDripSteps([...dripSteps, dripStepData]);
    setCreateDripStepModalVisible(false);
  };

  const checkValidFields = () => {
    if (!campaignName || !triggerType || !startDate || !endDate) {
      setError("Please fill all required fields.");
      setSuccess(false);
      return false;
    }

    if (dripSteps.length === 0) {
      setError("Please add at least one drip step.");
      setSuccess(false);
      return false;
    }

    if (!selectedSegment) {
      setError("Please select a segment.");
      setSuccess(false);
      return false;
    }

    setError("");
    return true;
  };

  const fetchSegments = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        AUDIENCE_SEGMENT_LIST_ENDPOINT + "?limit=1000",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch segments");
      }
      const jsonData = await response.json();

      setSegmentOptions(jsonData.results);
    } catch (error) {
      console.error("Error fetching segments:", error.message);
    }
  };

  const fetchSegmentAudienceCount = async () => {
    // If no segments are selected, reset phone number list and return
    if (!selectedSegment) {
      setSelectedSegmentAudienceCount(0);
      return;
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    let queryParams = new URLSearchParams({
      limit: 1,
      offset: 0,
    });

    selectedSegment.filters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(field, `${operator}:${value ?? ""}`);
    });

    try {
      const response = await fetch(
        `${AUDIENCE_COUNT_ENDPOINT}?${queryParams}`,
        { headers }
      );
      if (!response.ok)
        throw new Error("Failed to fetch segment audience count");
      const jsonData = await response.json();

      setSelectedSegmentAudienceCount(jsonData);
    } catch (error) {
      console.error("Error fetching segment audience count:", error.message);
    }
  };

  const formatButtonReplyOptions = (options) => {
    return Object.keys(options).map((key) => {
      return {
        button_id: key,
        value: options[key]?.value,
      };
    });
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  useEffect(() => {
    fetchSegmentAudienceCount();
  }, [selectedSegment]);

  const handleAddStepClick = () => {
    if (!triggerType) {
      setError("Please select a trigger type.");
      setSuccess(false);
      return;
    }
    setCreateDripStepModalVisible(true);
  };

  const handleErrors = (err) => {
    setSuccess(false);
    if (err.response?.data) {
      let message = "An error occurred";

      const data = err.response.data;

      // Handle drip_steps errors specially
      if (data.drip_steps && Array.isArray(data.drip_steps)) {
        message = data.drip_steps
          .map((stepObj, index) => {
            const stepMessages = Object.entries(stepObj)
              .map(([field, msgs]) => `${field}: ${msgs.join(", ")}`)
              .join("\n");
            return `Step ${index + 1}:\n${stepMessages}`;
          })
          .join("\n\n");
      } else if (typeof data === "object") {
        // Fallback for non-drip_steps errors
        const firstError = Object.values(data)[0];
        if (Array.isArray(firstError)) {
          message = Array.isArray(firstError[0])
            ? firstError[0][0]
            : firstError[0];
        } else if (typeof firstError === "string") {
          message = firstError;
        }
      } else {
        message = data;
      }

      setError(message);
    } else if (err.message) {
      setError("Failed to create tag: " + err.message);
    } else {
      setError("Failed to create tag.");
    }
  };

  const handlePublish = async () => {
    if (!checkValidFields()) {
      return;
    }

    setSuccess(false);
    setLoading(true);

    try {
      const payload = {
        name: campaignName,
        trigger_type: triggerType.value,
        start_date: dayjs(startDate).format("YYYY-MM-DD"),
        end_date: dayjs(endDate).format("YYYY-MM-DD"),
        description,
        segment_id: selectedSegment?.id,
        drip_steps: dripSteps.map((step, index) => {
          return {
            name: step?.stepName,
            template_id: step?.template?.id,
            delay_hours: step?.delay,
            step_order: index + 1,
            wait_for_reply: step?.waitForUserReply,
            button_reply_actions: formatButtonReplyOptions(
              step?.buttonReplyOptions
            ),
            on_no_reply: step?.noReplyOption?.value,
          };
        }),
      };

      const apiUrl = DRIP_CAMPAIGN_PUBLISH_ENDPOINT;

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.status !== 200 && response.status !== 201) {
        throw new Error("Error: " + response.data.error);
      }

      setSuccess(true);
      setTimeout(() => {
        onNavigate("drip_marketing");
        setSuccess(false);
      }, 1500);
    } catch (err) {
      handleErrors(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, px: 3 }}>
      <BackButton
        onClick={() => {
          onNavigate("drip_marketing");
        }}/>

      {/* Campaign Settings */}
      <Stack spacing={2} mb={4} mx="auto">
        <Typography variant="h6">Campaign Settings</Typography>

        <Stack direction="row" spacing={2}>
          <TextField
            label="Campaign Name"
            variant="outlined"
            size="small"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            fullWidth
            required
          />

          <Autocomplete
            sx={{ width: "100%" }}
            options={TRIGGER_TYPE_OPTIONS}
            value={triggerType}
            onChange={(event, newValue) => setTriggerType(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Trigger Type"
                variant="outlined"
                size="small"
                fullWidth
                required
              />
            )}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              value ? option.value === value.value : false
            }
            noOptionsText="No triggers found"
          />
        </Stack>

        {/* Duration */}
        <Stack direction="row" spacing={2}>
          {/* Start Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              format="DD-MM-YYYY"
              value={startDate || null}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: { size: "small", fullWidth: true, required: true },
              }}
              disablePast
            />
          </LocalizationProvider>

          {/* End Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              format="DD-MM-YYYY"
              value={endDate || null}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: { size: "small", fullWidth: true, required: true },
              }}
              disablePast
            />
          </LocalizationProvider>
        </Stack>

        <TextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
      </Stack>

      {/* Message Sequence */}
      <Stack spacing={1} mb={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            Message Sequence ({dripSteps?.length || 0} steps)
          </Typography>
          <Button
            onClick={handleAddStepClick}
            variant="contained"
            startIcon={<Add />}
            disabled={!triggerType}
          >
            Add Step
          </Button>
        </Stack>

        {dripSteps && dripSteps.length > 0 && (
          <Stack spacing={2}>
            {dripSteps.map((step, index) => (
              <DripStep
                key={index}
                step={step}
                onDelete={() => {
                  setDripSteps((prev) => prev.filter((_, i) => i !== index));
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Audience Selection */}
      <Stack spacing={2} mb={4}>
        <Typography variant="h6">Audience Selection</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Autocomplete
            sx={{ width: "100%" }}
            options={segmentOptions || []}
            value={selectedSegment}
            onChange={(event, newValue) => setSelectedSegment(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Audience Segments"
                variant="outlined"
                size="small"
                required
              />
            )}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No segments found"
          />
          <Typography sx={{ whiteSpace: "nowrap" }}>
            Audience Count: {selectedSegmentAudienceCount}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }}>
          {error.substring(0, 400) + (error.length > 400 ? "..." : "")}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ whiteSpace: "pre-wrap" }}>
          Drip Campaign Created Successfully
        </Alert>
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
        {/* <Button variant="outlined" onClick={handleSaveDraft} disabled={loading}>
          Save as Draft{" "}
          {loading && <CircularProgress size={12} sx={{ ml: 1 }} />}
        </Button> */}
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={loading || dripSteps?.length < 1}
        >
          Publish Campaign{" "}
          {loading && <CircularProgress size={12} sx={{ ml: 1 }} />}
        </Button>
      </Stack>

      {/* Create Drip Step Modal */}
      <CreateDripStepModal
        open={createDripStepModalVisible}
        handleClose={() => setCreateDripStepModalVisible(false)}
        triggerType={triggerType}
        addDripStep={addDripStep}
        firstStep={dripSteps?.length === 0}
      />
    </Box>
  );
};

export default CreateDripMarketingCampaign;
